/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useMemo, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import Pagination from "rc-pagination";

import {
  CommandBar,
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";

import { errorMessageStyles } from "Components/AddComment/AddComment.styles";
import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import ListCounts from "Components/Common/ListCounts/ListCounts";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import { Title } from "Components/DistributionListDetails/DistributionListDetails.styles";
import {
  commandBarStyles,
  updateCommandBarStyles,
} from "Components/ManageCodesList/ManageCodesList.styles";
import { ManageCodesSidePanel } from "Components/ManageCodesSidePanel/ManageCodesSidePanel";
import { containerStackStyles, PaddedFlexRow } from "Containers/Common/CommonContainer.styles";
import {
  getManageCodesList,
  setCurrentPage,
  setFilteredCount,
  setManageCodesList,
  setPageSize,
} from "Dataflows/Benefit/ManageCodesSlice";
import { IBenefitUploadModel } from "Interfaces/Benefits/IBenefitUploadModel";
import { useGetManageCodesListResponseQuery } from "Services/ManageCodesApi";
import { formatDate } from "Utility/FormatHelper";

/**
 * Update Student Ambassador Registration Status.
 * @returns ReactElement
 */
const ManageCodesList = (props): ReactElement => {
  const { filteredCount, pageSize, tenant, currentPage, benefitName } = props;
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // Adjust the path to your store file
  const manageCodesList = useSelector(getManageCodesList) || [];

  const modifyCodesClicked = (): void => {
    setIsPanelOpen(true);
  };

  const dispatch = useDispatch();

  const {
    data: manageCodesListResponse,
    isLoading: isManageCodesLoading,
    error: manageCodesError,
    refetch,
  } = useGetManageCodesListResponseQuery({
    tenant,
    query: {
      pageIndex: currentPage,
      pageSize: pageSize,
      benefitName: benefitName,
    },
  });

  useEffect(() => {
    if (!isPanelOpen) {
      refetch();
    }
  }, [isPanelOpen]);

  useEffect(() => {
    if (manageCodesListResponse) {
      dispatch(setFilteredCount(manageCodesListResponse.filteredCount));
      dispatch(setManageCodesList(manageCodesListResponse.benefitUploadsListViewModel));
    }
  }, [manageCodesListResponse, dispatch, isPanelOpen]);

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: "date",
        name: "Date",
        ariaLabel: "Date",
        fieldName: "date",
        minWidth: 160,
        isResizable: true,
        onRender: (item) => formatDate(item.date),
      },
      {
        key: "benefitName",
        name: "Benefit",
        ariaLabel: "Benefit",
        fieldName: "benefitName",
        minWidth: 160,
        isResizable: true,
      },
      {
        key: "quantity",
        name: "Quantity",
        ariaLabel: "quantity",
        fieldName: "quantity",
        minWidth: 160,
        isResizable: true,
      },
      {
        key: "updatedBy",
        name: "Updated by (alias)",
        ariaLabel: "Updated By",
        fieldName: "updatedBy",
        minWidth: 160,
        isResizable: true,
      },
      {
        key: "batchId",
        name: "Batch ID",
        ariaLabel: "Batch ID",
        fieldName: "batchId",
        minWidth: 160,
        isResizable: true,
      },
    ],
    []
  );

  /**
   * command Bar Items.
   */
  const commandBarItems = [
    {
      key: "addormodifyCodes",
      text: "Add/modify Codes",
      iconProps: { iconName: "Edit" },
      onClick: (): void => {
        modifyCodesClicked();
      },
    },
  ];

  if (isManageCodesLoading) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  if (manageCodesError)
    return (
      <Text variant="medium" styles={errorMessageStyles}>
        {("data" in manageCodesError && (manageCodesError.data as any)?.error?.message) || "An unexpected error occurred."}
      </Text>
    );

  return (
    <>
      <Stack styles={containerStackStyles}>
        <Stack>
          <PaddedFlexRow>
            <Title>Manage Codes</Title>
            <ListCounts total={filteredCount} loading={isManageCodesLoading} />
          </PaddedFlexRow>
        </Stack>
        <Stack styles={updateCommandBarStyles}>
          {
            <CommandBar
              items={commandBarItems}
              ariaLabel="Add/modify Codes"
              styles={commandBarStyles}
            />
          }
        </Stack>
        {isPanelOpen && (
          <ManageCodesSidePanel
            isPanelOpen={isPanelOpen}
            setIsPanelOpen={setIsPanelOpen}
            tenant={tenant}
          />
        )}
        <DetailsList
          items={manageCodesList.map((item: IBenefitUploadModel) => ({
            ...item,
            updatedBy: item.updatedBy?.replace("@microsoft.com", ""),
          }))}
          columns={columns}
          selectionMode={SelectionMode.none}
          getKey={(item: IBenefitUploadModel): string => `${item.date}${item.batchId}`}
          setKey="none"
          layoutMode={DetailsListLayoutMode.fixedColumns}
          isHeaderVisible={true}
          styles={detailListStyles}
        />
        <PaginationRow>
          <Pagination
            total={filteredCount}
            onChange={(page) => dispatch(setCurrentPage(page))}
            showTitle={false}
            current={currentPage}
            prevIcon={ChevronIcon("ChevronLeft")}
            nextIcon={ChevronIcon("ChevronRight")}
            pageSize={pageSize}
            defaultPageSize={props.defaultPageSize}
            showSizeChanger
          />
          <Dropdown
            options={[
              { key: 16, text: "Show 16 results" },
              { key: 32, text: "Show 32 results" },
              { key: 48, text: "Show 48 results" },
            ]}
            onChange={(_e, op): void => {
              dispatch(setPageSize(op?.key as number));
            }}
            selectedKey={pageSize}
            className="pagination-dropdown"
            title="Page Size Dropdown"
          />
        </PaginationRow>
      </Stack>
    </>
  );
};

export default ManageCodesList;
